// MARK://dhk8/pos/features/shell/LockOverlay
// Full-screen overlay shown when the terminal is locked (lock-store.locked).
// Reuses the SAME PIN-pad UI as LoginPage (the .dhk8-pin-* classes defined there are
// global). On submit it calls dhk8-platform-auth POST /v1/auth/pin-unlock with { pin }
// (Bearer) → { ok }. A 2xx unlocks; non-2xx shows "PIN incorrecto". Single phase
// (just the 6-digit clave) — the user is already known.

import { useCallback, useEffect, useState } from 'react'
import { Delete, Check, Lock } from 'lucide-react'

import { apiFetch, svcUrl } from '../../lib/api.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { useLockStore } from '../../stores/lock-store.js'
import { PREVIEW_STUB } from '../../lib/seed-preview.js'
import { DEMO_UNLOCK_PIN } from '../../lib/security-policy.js'
import BrandMark from '../../components/BrandMark.js'

const PASS_LENGTH = 6

export default function LockOverlay() {
  const locked = useLockStore((s) => s.locked)
  const unlock = useLockStore((s) => s.unlock)
  const user = useAuthStore((s) => s.user)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [shake, setShake] = useState(false)

  // Teclado físico — los cajeros sin pantalla touch necesitan teclear directo.
  // Captura 0-9 + Backspace + Escape mientras el overlay está montado y locked.
  // Va ANTES del early return para no romper rules-of-hooks.
  useEffect(() => {
    if (!locked) return
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault()
        setError(null)
        setPin((v) => (v.length < PASS_LENGTH ? v + e.key : v))
        return
      }
      if (e.key === 'Backspace') {
        e.preventDefault()
        setError(null)
        setPin((v) => v.slice(0, -1))
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setPin('')
        setError(null)
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
  }, [locked])

  // Validate a fully-typed PIN against dhk8-platform-auth. Wrapped in useCallback
  // so the auto-submit effect below can depend on a stable reference. Takes the
  // candidate PIN as an argument (not from closure) so it always validates the
  // exact 6 digits that triggered it — no stale-state race.
  const submit = useCallback(
    async (candidate: string) => {
      setBusy(true)
      setError(null)
      try {
        if (PREVIEW_STUB) {
          if (candidate === DEMO_UNLOCK_PIN) { unlock(); setPin(''); return }
          throw new Error('PIN incorrecto')
        }
        await apiFetch<{ ok: boolean }>(svcUrl('dhk8-platform-auth', '/v1/auth/pin-unlock'), {
          method: 'POST',
          body: { pin: candidate },
        })
        unlock()
        setPin('')
      } catch (e) {
        setShake(true)
        setTimeout(() => setShake(false), 320)
        setPin('')
        setError(e instanceof Error ? e.message : 'PIN incorrecto')
      } finally {
        setBusy(false)
      }
    },
    [unlock],
  )

  // Auto-submit once 6 digits are typed (matches LoginPage UX). MUST run as an
  // effect — NOT as a side-effect during render. Submitting during render fired
  // `submit()` on every render pass, so a re-render before `busy` committed (e.g.
  // a `hydrate()`/auth-store update racing the keypad) re-issued the unlock call
  // or interleaved its setState, leaving the overlay stuck on a correct PIN until
  // a full page refresh. As an effect it fires exactly once per completed PIN.
  useEffect(() => {
    if (locked && user && pin.length === PASS_LENGTH && !busy) {
      void submit(pin)
    }
  }, [locked, user, pin, busy, submit])

  if (!locked || !user) return null

  function handleDigit(d: string) {
    if (busy) return
    setError(null)
    setPin((v) => (v.length < PASS_LENGTH ? v + d : v))
  }
  function handleBackspace() {
    if (busy) return
    setError(null)
    setPin((v) => v.slice(0, -1))
  }
  function reset() { setPin(''); setError(null) }

  return (
    <div className="dhk8-lock-overlay" role="dialog" aria-modal="true" aria-label="Terminal bloqueada">
      <div className={`dhk8-pin-card${shake ? ' dhk8-pin-card--shake' : ''}`}>
        <div className="dhk8-pin-brand">
          <BrandMark size="lg" />
          <span className="dhk8-pin-tag">Bloqueada</span>
        </div>

        <div className="dhk8-pin-phase">
          <Lock size={14} />
          <span>PIN de {user.fullName}</span>
        </div>

        <div className="dhk8-pin-dots" aria-live="polite">
          {Array.from({ length: PASS_LENGTH }).map((_, i) => (
            <span key={i} className={`dhk8-pin-dot${i < pin.length ? ' dhk8-pin-dot--on' : ''}`} />
          ))}
        </div>

        {error && <div className="dhk8-pin-error">{error}</div>}

        <div className="dhk8-pin-keypad" aria-disabled={busy}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" className="dhk8-pin-key"
              onClick={() => handleDigit(d)} disabled={busy} aria-label={`Dígito ${d}`}>
              {d}
            </button>
          ))}
          <button type="button" className="dhk8-pin-key dhk8-pin-key--ghost"
            onClick={handleBackspace} disabled={busy} aria-label="Borrar">
            <Delete size={20} />
          </button>
          <button type="button" className="dhk8-pin-key"
            onClick={() => handleDigit('0')} disabled={busy} aria-label="Dígito 0">
            0
          </button>
          <button type="button" className="dhk8-pin-key dhk8-pin-key--ghost dhk8-pin-key--ok"
            onClick={reset} disabled={busy} aria-label="Reiniciar">
            <Check size={20} />
          </button>
        </div>
      </div>
    </div>
  )
}
